import { useEffect, useMemo } from "react"

import { Spin } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import EmptyComponent from "../../../../Components/Empty"
import { RoutesPaths } from "../../../../constants/user-routes"
import Sankey from "../../../DashboardPage/components/Sankey"
import { resetFilters, setFilters } from "../../../DisputesPage/redux/disputes.slice"
import { ANALYTICS_SECTION } from "../../redux/analytics.constants"
import { selectSectionData, selectSectionFetching } from "../../redux/analytics.slice"
import { FLOW_GROUPS, sankeyFlow } from "../../utils/disputeFlow"
import Section from "../Section"

// Sankey node id -> the /recover/all filter its drill applies. Derived from the flow
// definition itself (FLOW_GROUPS), so a stage added or renamed there cannot leave a node
// that drills to the wrong list: a group node expands to every status underneath it, a
// stage node to its own. Lane-prefixed ids from the split version add the Type filter on
// top of the same status set.
const SEGMENT_TO_STATUSES = (() => {
  const out = { all: [] }
  const walk = (branch) => {
    const stages = (branch.groups || []).flatMap((group) =>
      group.stages?.length ? group.stages : [group]
    )
    // A branch/group node drills to every status underneath it; a leaf to its own.
    out[branch.id] = stages.length ? stages.flatMap((stage) => stage.match) : branch.match || []
    for (const group of branch.groups || []) {
      // A group with no stages is a leaf itself (Escalated), so it drills by its own match.
      out[group.id] = group.stages?.length
        ? group.stages.flatMap((stage) => stage.match)
        : group.match || []
      for (const stage of group.stages || []) out[stage.id] = stage.match
    }
  }
  FLOW_GROUPS.forEach(walk)
  return out
})()

const LANE_TYPES = { d: "dispute", i: "inquiry" }

// A node from the split version carries its lane prefix ("d_won", "i_root"); the drill is
// then that lane's Type plus whatever the underlying node filters by.
const filterFor = (segment) => {
  const laneMatch = /^([di])_(.+)$/.exec(segment || "")
  if (!laneMatch) return { status: SEGMENT_TO_STATUSES[segment] || [] }
  const [, prefix, rest] = laneMatch
  const dispute_type = [LANE_TYPES[prefix]]
  if (rest === "root") return { dispute_type }
  return { dispute_type, status: SEGMENT_TO_STATUSES[rest] || [] }
}

// Blue gradient ramp across the flow: nodes are coloured by column (deep at the
// source -> bright at the outcomes) and the link gradient (source->target) blends
// each ribbon, so the whole Sankey reads as one continuous blue gradient. Endpoints
// are on-palette and theme-keyed so it stays legible on the white and dark cards
// (light: blueDeep #072F94 -> bluePrimary #225AEA; dark: blueDepressed #1448CB ->
// bluePrimary #225AEA, kept brighter so the bars don't sink into the dark card).
const RAMP = {
  light: ["#072F94", "#225AEA"],
  dark: ["#1448CB", "#225AEA"]
}

// The split version's inquiry lane takes the palette's purple ramp (@purpleDepressed ->
// @purplePrimary). Two lanes of the same blue read as one flow that happens to have a gap
// in it; a second hue is what makes the split legible without labelling every node twice.
const RAMP_INQUIRY = {
  light: ["#460D90", "#661AC6"],
  dark: ["#460D90", "#7B2FD6"]
}

const lerpHex = (a, b, t) => {
  const ca = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16))
  const cb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16))
  const mix = ca.map((v, i) => Math.round(v + (cb[i] - v) * t))
  return `#${mix.map((v) => v.toString(16).padStart(2, "0")).join("")}`
}

// Longest-path depth per node id (column index), from the link structure.
const nodeDepths = (nodes, links) => {
  const parents = new Map()
  links.forEach((l) => {
    if (!parents.has(l.target)) parents.set(l.target, [])
    parents.get(l.target).push(l.source)
  })
  const depth = new Map()
  const resolve = (id, seen) => {
    if (depth.has(id)) return depth.get(id)
    const ps = parents.get(id)
    if (!ps || ps.length === 0) {
      depth.set(id, 0)
      return 0
    }
    if (seen.has(id)) return 0
    seen.add(id)
    const d = 1 + Math.max(...ps.map((p) => resolve(p, seen)))
    depth.set(id, d)
    return d
  }
  nodes.forEach((n) => resolve(n.id, new Set()))
  return depth
}

// Map our { nodes:[{id,label,volume}], links:[{source:id,...}] } flow onto the shared
// Sankey's contract: nodes { name, color, segment, volume }, links indexed into nodes.
// Each node's colour is sampled from the theme ramp by its column depth.
const toChartData = (flow, t, ramp, inquiryRamp) => {
  const nodes = flow?.nodes || []
  const links = flow?.links || []
  const indexOf = new Map(nodes.map((n, i) => [n.id, i]))
  const depths = nodeDepths(nodes, links)
  const maxDepth = Math.max(1, ...depths.values())
  return {
    nodes: nodes.map((n) => {
      // Lane-prefixed ids come from the split version; "i_" is the inquiry lane.
      const laneRamp = n.id.startsWith("i_") ? inquiryRamp : ramp
      return {
        name: t(n.label),
        color: lerpHex(laneRamp[0], laneRamp[1], (depths.get(n.id) || 0) / maxDepth),
        segment: n.id,
        volume: n.volume
      }
    }),
    links: links
      .filter((l) => indexOf.has(l.source) && indexOf.has(l.target))
      .map((l) => ({
        source: indexOf.get(l.source),
        target: indexOf.get(l.target),
        value: l.value,
        volume: l.volume
      }))
  }
}

// "Dispute flow": the chart-card shell wrapping the shared SVG Sankey, laid out by sankeyFlow()
// from flow/'s counts. Always split by case type: an inquiry carries no financial activity
// while a dispute is a debit from the merchant's account, so blending the two into one root
// hid the distinction that matters most for their books.
const DisputeFlow = ({ getData }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const theme = useSelector((s) => s.nav.theme)
  const isMobile = useSelector((s) => s.nav.isMobile)
  const flow = useSelector(selectSectionData(ANALYTICS_SECTION.FLOW))
  const fetching = useSelector(selectSectionFetching(ANALYTICS_SECTION.FLOW))

  useEffect(() => {
    getData(ANALYTICS_SECTION.FLOW)
  }, [getData])

  const data = useMemo(() => sankeyFlow(flow?.result, { splitByType: true }), [flow])
  const hasData = data.nodes.length > 0
  const chartData = useMemo(
    () =>
      toChartData(
        data,
        t,
        theme === "dark" ? RAMP.dark : RAMP.light,
        theme === "dark" ? RAMP_INQUIRY.dark : RAMP_INQUIRY.light
      ),
    [data, t, theme]
  )

  // Drill-in: pre-filter the existing disputes list to the clicked segment and navigate
  // there, reusing the same Redux filter the status cards use (no new route).
  const drillTo = (segment) => {
    dispatch(resetFilters())
    dispatch(setFilters(filterFor(segment)))
    navigate(RoutesPaths.recover.all)
  }

  return (
    <Section
      title={t("Dispute flow")}
      tooltip={t(
        "How your disputes and inquiries flow from intake through the fight pipeline to a final outcome. The two are separated because an inquiry carries no financial activity, while a dispute is a debit from your account."
      )}
    >
      <Spin spinning={fetching}>
        {hasData ? (
          <Sankey
            data={chartData}
            theme={theme}
            isMobile={isMobile}
            unit={t("disputes")}
            onNodeClick={(node) => drillTo(node.segment)}
            onLinkClick={(targetNode) => drillTo(targetNode.segment)}
          />
        ) : (
          <EmptyComponent type="no_data_available" />
        )}
      </Spin>
    </Section>
  )
}

export default DisputeFlow
